/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {useContext} from "react";
import {COLORS} from "@res/colors";
import {ChatBoxMessage} from "./ChatBox";
import * as emotion from "emotion";
import {FONTS} from "@res/fonts";
import {ICONS} from "@res/icons";
import {hideTooltip, showTooltipAtXY} from "../../widget/appTooltip";
import {DB_Message, MessageMap, MessageStatuses} from "@app/ir-q-app-common/types/message";
import {MessagingModule} from "@modules/MessagingModule";
import {UsersModule} from "@modules/UsersModule";
import {AudioWavePlayer} from "../../widget/speech-audio/AudioWavePlayer";
import {IconStatus} from "../../../utils/IconStatus";
import {margin_l_sm} from "@styles/styles";
import {formatDateTime} from "@app/app-shared/days";
import {PatientsContext} from "../patients/PatientsContext";
import {MessageWithStatus} from "@app/ir-q-app-common/types/message-api";
import {ChatsContext} from "./ChatsContext";
import {ContactsContext} from "./ContactsContext";

const replyStyle = emotion.css(
    {
        backgroundColor: COLORS.very_light_blue,
        borderRadius: 4,
        padding: "10px 15px"
    }
);

const messageWrapper = emotion.css(
    {
        marginTop: 20,
        marginBottom: 20
    }
);

const messageTextLine = emotion.css(
    {
        marginTop: 5,
        marginBottom: 5
    }
);

export const MessageEntry = (props: ChatBoxMessage) => {
    const pCtx = useContext(PatientsContext);
    const cCtx = useContext(ChatsContext);
    const {contacts} = useContext(ContactsContext);

    const showWaitingResponseTooltip = (e: React.MouseEvent) => {
        const hourglass_tooltip = <div>
            {FONTS.reg_14("Waiting for customer's reply")}
        </div>;
        const rect = e.currentTarget.getBoundingClientRect();
        const x = rect.x + rect.width + 5;
        const y = rect.y;
        showTooltipAtXY(hourglass_tooltip, x, y, {
            padding: 10,
            backgroundColor: COLORS.white,
            zIndex: 30,
            border: `1px solid ${COLORS.primary_light_blue}`
        });
    };

    const renderMessageData = (failed = false) => {
        const message = props.message;
        if (MessagingModule.needsToFetchSignedUrl(message)) {
            // The chat's group contact — same "the other participant" derivation ChatBox already
            // does to pick a sender. The backend needs it to authorize the signing request.
            const groupContactId = cCtx.chats?.[message.chatId]?.contactIds.find(_id => _id !== props.patientContactId);
            if (groupContactId)
                MessagingModule.getReadSignedUrl(message._id, message.chatId, groupContactId).then(response => {
                    if (message && message.type !== "text") {
                        message.data = response.data;
                        cCtx.updateMessages([message]);
                    }
                }).catch(console.error);

            return <div style={{display: "flex", justifyContent: "center", marginBottom: 10}}>
                {FONTS.reg_16_secondary_dark("Loading media...")}
            </div>;
        }

        switch (message.type) {
            case "image":
            case "video":
                return renderMediaMessage(message);
            case "audio":
                return renderAudioMessage(message);
            case "text":
                return renderTextMessage(failed, message);
            default:
                return renderDefaultFallback(message);
        }
    };

    const renderTextMessage = (failed: boolean, _message: MessageWithStatus) => {
        return failed ? FONTS.reg_16_secondary_red(_message.data.text) : FONTS.reg_16_secondary_dark(_message.data.text);
    }

    const renderDefaultFallback = (_message: DB_Message<any>) => {
        return FONTS.reg_16_secondary_red(`You received a ${_message.type}, we will be able to show it shortly in the upcoming feature`);
    };

    const renderSenderName = () => {
        let senderName;
        if (props.message.metadata?.ghostUserId) {
            const user = UsersModule.getUser(props.message.metadata?.ghostUserId);
            senderName = `${user?.first} ${user?.last}`;
        } else {
            const patient = Object.values(pCtx.patients).find(_patient => _patient?.contactId === props.message.senderId);
            if (patient)
                senderName = patient.name;
            else {
                const contact = contacts[props.message.senderId];
                senderName = `${contact?.firstName} ${contact?.lastName}`;
            }
        }

        return <div>
            {FONTS.medium_16(senderName || "")}
        </div>;
    };

    const renderUserMessages = () => {
        const message = props.message;
        const answered = props.answered;

        const failed = message.status.status.toUpperCase() === MessageStatuses.FAILED.toUpperCase();
        return <div className={`ll_v_s`}>
            <div className={`ll_h_c match_width`} style={{justifyContent: "space-between"}}>
                <div className={`ll_h_c`} style={{width: "100%", justifyContent: "space-between"}}>
                    <div style={{display: "flex", alignItems: "center"}}>
                        {answered &&
                            <div className={`ll_v_l`} style={{marginLeft: -5, marginRight: 10}}
                                 onMouseEnter={showWaitingResponseTooltip}
                                 onMouseLeave={() => hideTooltip()}>
                                {ICONS.icon_hourglass(COLORS.neutral_text_dark_2, 24)}
                            </div>}
                        {renderSenderName()}
                    </div>
                    <div style={{display: "flex", alignItems: "center"}}>
                        {props.answered ? FONTS.reg_14_secondary_dark("Answered") : <IconStatus status={message.status.status}/>}
                        <div className={`${margin_l_sm}`}>
                            {
                                FONTS.reg_14_secondary_dark(formatDateTime(
                                    message.timestamp, "MMM D | hh:mm A", true, true,
                                    false, " | hh:mm A"
                                ))
                            }
                        </div>
                    </div>
                </div>
            </div>
            <div className={`${messageTextLine}`}>
                {renderMessageData(failed)}
            </div>
        </div>
    };

    // Can be a text, or an audio reply.
    const renderPatientMessages = () => {
        const message = props.message;
        return <div className={`ll_h_c`} style={{flexFlow: "row-reverse"}}>
            <div className={`ll_v_r ${replyStyle}`}>
                <div>
                    {
                        FONTS.reg_14_secondary_dark(formatDateTime(
                            message.timestamp, "MMM D | hh:mm A", true, true,
                            false, " | hh:mm A"
                        ))
                    }
                </div>
                <div className={`${messageTextLine}`}>
                    {renderMessageData()}
                </div>
            </div>
        </div>;
    };

    const renderAudioMessage = (message: DB_Message<"audio">) => {
        return <div style={{width: 400, marginTop: 5}}>
            <div style={{display: "flex", alignItems: "center", gap: 8}}>
                <div style={{flex: 1}}>
                    {renderAudioWave(message)}
                </div>
                {renderDownloadButton(message)}
            </div>
            {message.data.text && renderTranscript(message.data.text)}
        </div>;
    };

    const renderMediaMessage = (message: DB_Message<"video" | "image">) => {
        return <div style={{width: 400, marginTop: 5, display: "grid"}}>
            {getMediaTag(message.type)}
            {message.data.attachedText && FONTS.reg_16_secondary_dark(message.data.attachedText)}
        </div>;
    };

    const getMediaTag = (messageType: keyof MessageMap) => {
        const message = props.message;
        const messageMedia = MessagingModule.getMessageMedia(message._id);
        const url = message.data.signedUrl;

        const src = messageMedia ? URL.createObjectURL(messageMedia) : url;
        switch (messageType) {
            case "image":
                return <img style={{maxWidth: 400, maxHeight: 300}} src={src} alt={"image-in-chat"}/>;
            case "video":
                return <video style={{
                    maxWidth: 400,
                    maxHeight: 300
                }} src={src} controls={true} autoPlay={false}/>;
        }
    };

    const renderTranscript = (text: string) => {
        return <div style={{width: "100%", paddingLeft: 5, paddingRight: 5, boxSizing: "border-box"}}>
            {
                FONTS.reg_16_secondary_dark("Transcript:", {color: COLORS.steelGray})
            }
            {" "}
            {
                FONTS.reg_16_secondary_dark(text)
            }
        </div>;
    };

    const renderAudioWave = (_message: DB_Message<"audio">) => {
        const messageMedia = MessagingModule.getMessageMedia(_message._id);
        const url = _message.data.signedUrl;

        return <div style={{width: "100%", height: 50, marginBottom: 15}}>
            <AudioWavePlayer
                url={url}
                blob={messageMedia}
            />
        </div>;
    };

    const renderDownloadButton = (_message: DB_Message<"audio">) => {
        const handleDownload = () => {
            const messageMedia = MessagingModule.getMessageMedia(_message._id);
            const url = _message.data.signedUrl;

            if (messageMedia) {
                const blobUrl = URL.createObjectURL(messageMedia);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = `audio_${_message._id}.mp3`;
                link.click();
                URL.revokeObjectURL(blobUrl);
            } else if (url) {
                const link = document.createElement('a');
                link.href = url;
                link.download = `audio_${_message._id}.mp3`;
                link.target = '_blank';
                link.click();
            }
        };

        return <div
            style={{cursor: "pointer", display: "flex", alignItems: "center"}}
            onClick={handleDownload}
        >
            {ICONS.icon_download(COLORS.primary_blue, 24)}
        </div>;
    };

    const message = props.message;

    return <div className={messageWrapper}>
        {
            message.senderId === props.patientContactId ?
                renderPatientMessages() :
                renderUserMessages()
        }
    </div>
}
